import React, { useState, useEffect } from 'react';
import { useTypedTranslation } from '@app/hooks';
import { basicService, notificationService } from '@app/services';
import { IMessageDetail } from '@app/models';
import { DATE_TIME_FULL_FORMAT, passlyFormatDate, fromNow, genericErrorToast } from '@app/helpers';
import marked from 'marked';
import DOMPurify from 'dompurify';
import { useDispatch } from 'react-redux';
import { removeDetailsBreadcrumb } from '@app/store/actions';
import { Link } from '@material-ui/core';
import PageLoader from '@app/components/PageLoader/pageLoader.component';

const FILE_TO_DOWNLOAD_NAME = 'Export.csv';

const NotificationDetailsPage = (props: any) => {
    const { translation } = useTypedTranslation();
    const dispatch = useDispatch();

    const [message, setMessage] = useState<IMessageDetail | undefined>(undefined);
    const [properties, setProperties] = useState<{fileId: string; messageType: string } | undefined>();
    const [messageCreatedFormatted, setMessageCreatedFormatted] = useState('');
    const [messageCreatedFromNow, setMessageCreatedFromNow] = useState('');
    const [messageBodyFormatted, setMessageBodyFormatted] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [homerealm] = useState(window.location.hostname);

    const notificationId: string = props.match.params.id;

    useEffect(() => {
        notificationService.getMessage(notificationId, genericErrorToast, handleSuccess);

        return () => {
            dispatch(removeDetailsBreadcrumb());
        };
    }, []);

    const handleSuccess = (response: any) => {
        const results: IMessageDetail = response.data;

        setMessageBodyFormatted(results.Body.replace(/\${homerealm}/gi, homerealm));
        setMessageCreatedFormatted(passlyFormatDate(results.Created, DATE_TIME_FULL_FORMAT));
        setMessageCreatedFromNow(fromNow(results.Created));

        setMessage(results);
        if (results.Properties) {
            setProperties(JSON.parse(results.Properties));
        }
        if (!results.IsRead) {
            notificationService.markMessageAsRead(notificationId, { IsRead: true, Id: notificationId }, genericErrorToast);
        }
    };

    const getMessageFrom = (sender: string | undefined) => {
        if (!sender) {
            return translation('SYSTEM_MSG_SENDER');
        }

        return sender;
    };

    const getMarkdownText = () => {
        const rawMarkup = DOMPurify.sanitize(marked.parse(messageBodyFormatted));
        return { __html: rawMarkup };
    };
    const getFile = () => {
        if (properties?.fileId) {
            const url = `/api/export/download?fileid=${properties?.fileId}`;

            setIsLoading(true);

            basicService.getFile( url, FILE_TO_DOWNLOAD_NAME).then(() => {
                setIsLoading(false);
            }).catch(() => {
                setIsLoading(false);
            });
        }
    };

    return (
        <>
            <div className='notificationdetails'>
                <div className='notificationdetails__message-container'>
                    <div className='notificationdetails__header'>
                        <div className='notificationdetails__headings'>
                            <span>{translation('MSG_TO')}</span>
                            <span>{message?.Recipient.DisplayName}</span>
                        </div>

                        <div className='notificationdetails__headings'>
                            <span>{translation('MSG_FROM')}</span>
                            <span>{getMessageFrom(message?.Sender)}</span>
                        </div>
                        <div className='notificationdetails__headings'>
                            <span>{translation('MSG_SUBJECT')}</span>
                            <span>{message?.Subject}</span>
                        </div>
                        <div className='notificationdetails__headings'>
                            <span>{translation('MSG_RECEIVED')}</span>
                            <span>{messageCreatedFormatted}</span>
                            <span className='notificationdetails__friendly-timestamp'>&nbsp;({messageCreatedFromNow})</span>
                        </div>
                    </div>
                    <div className='notificationdetails__body'>
                        <div dangerouslySetInnerHTML={getMarkdownText()} />
                        {properties?.messageType === 'downloadFile' ? (
                            <>
                                <Link onClick={getFile}>
                                    {translation('DOWNLOAD_CSV')}
                                </Link>
                            </>
                        ) : null}
                    </div>
                </div>
            </div>
            {isLoading && <PageLoader useOverlay={true} />}
        </>
    );
};

export default NotificationDetailsPage;